import type { CouleurAPI } from "@/types/mel-components";

export type BasicColorFamilyId =
  | "beige"
  | "blanc"
  | "bleu"
  | "gris"
  | "jaune"
  | "marron"
  | "noir"
  | "orange"
  | "rose"
  | "rouge"
  | "vert"
  | "violet"
  | "multicolore"
  | "autres";

export interface BasicColorFamily {
  id: BasicColorFamilyId;
  label: string;
  /** Pastille de la famille (sidebar) */
  swatch: string;
  /** Mots-clés pour rattacher une couleur vendeur (ordre = priorité) */
  keywords: string[];
}

/**
 * Couleurs basiques affichées à gauche.
 * L'ordre compte pour le matching : plus spécifique d'abord (beige avant blanc).
 */
export const BASIC_COLOR_FAMILIES: BasicColorFamily[] = [
  {
    id: "beige",
    label: "Beige",
    swatch: "#D4C4A8",
    keywords: [
      "beige",
      "écru",
      "ecru",
      "ivoire",
      "vanille",
      "nude",
      "crème",
      "creme",
      "taupe",
      "sable",
      "champagne",
      "lin",
      "ocre",
    ],
  },
  {
    id: "rouge",
    label: "Rouge",
    swatch: "#C0392B",
    keywords: [
      "rouge",
      "bordeaux",
      "cerise",
      "corail",
      "vermillon",
      "écarlate",
      "ecarlate",
      "cramoisi",
      "burgundy",
      "brique",
      "carmin",
    ],
  },
  {
    id: "rose",
    label: "Rose",
    swatch: "#E8A0BF",
    keywords: [
      "rose",
      "blush",
      "fuchsia",
      "magenta",
      "framboise",
      "vieux rose",
      "pink",
    ],
  },
  {
    id: "orange",
    label: "Orange",
    swatch: "#E67E22",
    keywords: [
      "orange",
      "pêche",
      "peche",
      "abricot",
      "saumon",
      "mandarine",
      "cuivre",
      "rouille",
      "terracotta",
    ],
  },
  {
    id: "jaune",
    label: "Jaune",
    swatch: "#F1C40F",
    keywords: [
      "jaune",
      "moutarde",
      "citron",
      "poussin",
      "doré",
      "dore",
      "gold",
    ],
  },
  {
    id: "vert",
    label: "Vert",
    swatch: "#27AE60",
    keywords: [
      "vert",
      "olive",
      "kaki",
      "menthe",
      "émeraude",
      "emeraude",
      "sapin",
      "bouteille",
      "céladon",
      "celadon",
      "pomme",
    ],
  },
  {
    id: "bleu",
    label: "Bleu",
    swatch: "#2980B9",
    keywords: [
      "bleu",
      "blue",
      "marine",
      "navy",
      "cyan",
      "turquoise",
      "ciel",
      "océan",
      "ocean",
      "pétrole",
      "petrole",
      "denim",
      "jeans",
      "indigo",
      "nocturne",
      "canard",
      "electric",
      "electrique",
      "teal",
      "pacific",
    ],
  },
  {
    id: "violet",
    label: "Violet",
    swatch: "#8E44AD",
    keywords: ["violet", "lavande", "lilas", "mauve", "prune"],
  },
  {
    id: "marron",
    label: "Marron",
    swatch: "#8B5A2B",
    keywords: [
      "marron",
      "caramel",
      "brun",
      "chocolat",
      "choco",
      "camel",
      "cognac",
      "café",
      "cafe",
      "tan",
      "mocha",
      "bronze",
    ],
  },
  {
    id: "noir",
    label: "Noir",
    swatch: "#1A1A1A",
    keywords: ["noir", "black", "carbone"],
  },
  {
    id: "blanc",
    label: "Blanc",
    swatch: "#F5F5F5",
    keywords: ["blanc", "white", "off white", "off-white", "transparent"],
  },
  {
    id: "gris",
    label: "Gris",
    swatch: "#95A5A6",
    keywords: [
      "gris",
      "argent",
      "anthracite",
      "silver",
      "grey",
      "gray",
      "acier",
      "slate",
      "pearl",
      "ice",
    ],
  },
  {
    id: "multicolore",
    label: "Multicolore",
    swatch:
      "linear-gradient(135deg, #E74C3C 0%, #F1C40F 25%, #2ECC71 50%, #3498DB 75%, #9B59B6 100%)",
    keywords: ["multicolore", "multicolor", "bicolore", "bicolor"],
  },
  {
    id: "autres",
    label: "Autres",
    swatch: "#CCCCCC",
    keywords: [],
  },
];

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Match mot entier (évite « or » dans « corail », « tan » dans « taupe »…). */
function keywordMatches(nom: string, keyword: string): boolean {
  const k = normalize(keyword);
  if (!k) return false;
  return new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(k)}(?:$|[^a-z0-9])`).test(
    nom,
  );
}

/** Découpe "Noir/Beige", "rouge - blanc", etc. */
export function splitCompoundColorName(colorName: string): string[] {
  const raw = (colorName || "").trim();
  if (!raw) return [];
  // Uniquement / et | — le tiret casse trop de libellés (off-white, bleu-marine…)
  const parts = raw
    .split(/\s*[/|]\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts : [raw];
}

export function isCompoundColorName(colorName: string): boolean {
  return splitCompoundColorName(colorName).length >= 2;
}

/** Multicolore / Bicolore (catalogue officiel ou nom composé Noir/Beige). */
export function isMulticoloreFamilyColor(colorName: string): boolean {
  const nom = normalize(colorName);
  if (!nom) return false;
  if (isCompoundColorName(colorName)) return true;
  return (
    nom === "multicolore" ||
    nom === "multicolor" ||
    nom === "bicolore" ||
    nom === "bicolor" ||
    nom.includes("multicolore") ||
    nom.includes("multicolor")
  );
}

/** Famille d'un libellé simple (sans composition). */
function matchSimpleFamilyId(part: string): BasicColorFamilyId | null {
  const nom = normalize(part);
  if (!nom) return null;

  // Mots-clés plus longs d'abord (ex. « vieux rose » avant « rose »)
  const ranked = BASIC_COLOR_FAMILIES.filter(
    (f) => f.id !== "autres" && f.id !== "multicolore",
  ).flatMap((family) =>
    family.keywords.map((keyword) => ({
      familyId: family.id,
      keyword,
      len: normalize(keyword).length,
    })),
  );
  ranked.sort((a, b) => b.len - a.len);

  for (const { familyId, keyword } of ranked) {
    if (keywordMatches(nom, keyword)) return familyId;
  }

  for (const family of BASIC_COLOR_FAMILIES) {
    if (family.id === "autres" || family.id === "multicolore") continue;
    if (nom === normalize(family.label)) return family.id;
  }
  return null;
}

export function getFamilySwatch(familyId: BasicColorFamilyId): string {
  return (
    BASIC_COLOR_FAMILIES.find((f) => f.id === familyId)?.swatch || "#CCCCCC"
  );
}

/**
 * Pastille visuelle : unie, ou coupée en 2/3 si nom composé (Noir/Beige).
 * Une seule pastille — jamais un doublon par famille.
 */
export function getColorSwatchStyle(
  colorName: string,
  hexColor?: string | null,
): Record<string, string> {
  const parts = splitCompoundColorName(colorName);

  if (parts.length >= 2) {
    const swatches = parts
      .map((part) => {
        const familyId = matchSimpleFamilyId(part);
        return familyId ? getFamilySwatch(familyId) : null;
      })
      .filter((s): s is string => !!s && !s.startsWith("linear-gradient"));

    if (swatches.length >= 2) {
      const unique = swatches.slice(0, 3);
      const slice = 100 / unique.length;
      const stops = unique
        .map((color, i) => {
          const from = Math.round(i * slice);
          const to = Math.round((i + 1) * slice);
          return `${color} ${from}%, ${color} ${to}%`;
        })
        .join(", ");
      return { backgroundImage: `linear-gradient(90deg, ${stops})` };
    }
  }

  // Couleur simple : hex vendeur en priorité, sinon pastille famille
  if (hexColor) {
    return { backgroundColor: hexColor };
  }

  if (normalize(colorName) === "transparent") {
    return {
      backgroundColor: "#FFFFFF",
      backgroundImage:
        "linear-gradient(45deg, #CCCCCC 25%, transparent 25%, transparent 75%, #CCCCCC 75%, #CCCCCC), linear-gradient(45deg, #CCCCCC 25%, transparent 25%, transparent 75%, #CCCCCC 75%, #CCCCCC)",
      backgroundSize: "10px 10px",
      backgroundPosition: "0 0, 5px 5px",
    };
  }

  if (normalize(colorName) === "bicolore") {
    return {
      backgroundImage: "linear-gradient(90deg, #1A1A1A 50%, #FFFFFF 50%)",
    };
  }

  if (
    normalize(colorName) === "multicolore" ||
    normalize(colorName) === "multicolor"
  ) {
    return {
      backgroundImage:
        "linear-gradient(135deg, #E74C3C 0%, #F1C40F 25%, #2ECC71 50%, #3498DB 75%, #9B59B6 100%)",
    };
  }

  const familyId = matchSimpleFamilyId(colorName);
  return {
    backgroundColor: familyId ? getFamilySwatch(familyId) : "#CCCCCC",
  };
}

export function getBasicFamilyIdForColorName(
  colorName: string,
): BasicColorFamilyId {
  if (isMulticoloreFamilyColor(colorName)) {
    return "multicolore";
  }

  return matchSimpleFamilyId(colorName) || "autres";
}

/** Libellé famille eFashion → famille UI du picker. */
export function mapEfashionLabelToBasicFamilyId(
  label: string | null | undefined,
): BasicColorFamilyId | null {
  if (!label?.trim()) return null;
  const nom = normalize(label);
  const map: Record<string, BasicColorFamilyId> = {
    noir: "noir",
    blanc: "blanc",
    beige: "beige",
    bleu: "bleu",
    gris: "gris",
    marron: "marron",
    orange: "orange",
    rouge: "rouge",
    rose: "rose",
    jaune: "jaune",
    "or / argent": "jaune",
    "or/argent": "jaune",
    vert: "vert",
    violet: "violet",
    motifs: "multicolore",
  };
  return map[nom] ?? null;
}

/** IDs historiques couleurs_efashion → famille UI. */
export const EFASHION_ID_TO_BASIC_FAMILY: Record<number, BasicColorFamilyId> = {
  1: "noir",
  2: "blanc",
  3: "beige",
  4: "bleu",
  5: "gris",
  6: "marron",
  7: "orange",
  8: "rouge",
  9: "rose",
  10: "jaune",
  11: "jaune", // Or / Argent
  12: "vert",
  13: "violet",
  14: "multicolore", // Motifs
};

/** Famille UI : famille eFashion (libellé ou id) en priorité, sinon matching du nom. */
export function getBasicFamilyIdForColor(
  color: Pick<
    CouleurAPI,
    "couleur_FR" | "familleEfashion" | "idCouleurEfashion"
  >,
): BasicColorFamilyId {
  const fromLabel = mapEfashionLabelToBasicFamilyId(color.familleEfashion);
  if (fromLabel && fromLabel !== "multicolore") return fromLabel;
  if (
    fromLabel === "multicolore" &&
    isMulticoloreFamilyColor(color.couleur_FR || "")
  ) {
    return "multicolore";
  }

  const efashionId = Number(color.idCouleurEfashion);
  if (Number.isFinite(efashionId) && efashionId > 0) {
    const fromId = EFASHION_ID_TO_BASIC_FAMILY[efashionId];
    if (fromId && fromId !== "multicolore") return fromId;
    if (
      fromId === "multicolore" &&
      isMulticoloreFamilyColor(color.couleur_FR || "")
    ) {
      return "multicolore";
    }
  }

  return getBasicFamilyIdForColorName(color.couleur_FR || "");
}

export function groupVendorColorsByBasicFamily(
  colors: CouleurAPI[],
): Record<BasicColorFamilyId, CouleurAPI[]> {
  const groups = Object.fromEntries(
    BASIC_COLOR_FAMILIES.map((f) => [f.id, [] as CouleurAPI[]]),
  ) as Record<BasicColorFamilyId, CouleurAPI[]>;

  for (const color of colors) {
    const familyId = getBasicFamilyIdForColor(color);
    groups[familyId].push(color);
  }

  for (const family of BASIC_COLOR_FAMILIES) {
    groups[family.id].sort((a, b) =>
      (a.couleur_FR || "").localeCompare(b.couleur_FR || "", "fr"),
    );
  }

  return groups;
}

export function getFamiliesWithVendorColors(
  colors: CouleurAPI[],
): BasicColorFamily[] {
  const groups = groupVendorColorsByBasicFamily(colors);
  return BASIC_COLOR_FAMILIES.filter((f) => (groups[f.id]?.length ?? 0) > 0);
}
